/**
 * Party-facts lever D — **the `parties` panel field**: who is on this deal, by firm.
 *
 * Replaces the two flat catch-all columns (`all_orgs` / `all_people`) IN THE PANEL when the list
 * config carries `parties` (D-5, owner 2026-09-05; the grid keeps the flat columns). The shape is the
 * spec's: the firm's own chips on the heading line, its people nested beneath, three shown with
 * `+N more`, `Name, Title` per person, and a role chip on a person only where their set differs from
 * the firm's.
 *
 * **Everything it renders was decided by the server.** Order comes from `materiality_rank` /
 * `side_rank` / `activity` / `substantive`, the badge from `contradiction`, the fold from `implied` —
 * all computed after the entity gate, the D8 audience and the §9 evidence gate. This component adds
 * no rule of its own; its only decision is which of the server's orderings to apply, and that is
 * `crmPartiesSort.sortPartyTree`, a pure function (build-plan invariant 10).
 *
 * ⚠ **A payload with no `viewer_enriched` marker renders nothing.** The gated-but-un-enriched cell
 * reaches the grid rows (`party_tree` is `grid: False`, so nothing there renders it, but the blob key
 * still ships), and drawing it would print zeroes for "not counted" — absent numbers and zero numbers
 * are different claims. The panel's own read (`get_record` / `record_review.v2`) always enriches.
 */
import { useCallback, useMemo, useState } from 'react';

import {
  type PartiesSort, type PartyGroupNode, type PartyPersonNode, type PartyRoleEntry,
  type PartyTreeValue, type SortMode, type SortWindow,
  DEFAULT_SORT, INDIVIDUALS_NODE_ID, PEOPLE_SHOWN, SORT_WINDOWS,
  distinctPersonRoles, isPartyTree, lifetimeLabel, loadPartiesSort, savePartiesSort,
  sortPartyTree, sortRoleEntries,
} from './crmPartiesSort';
import {
  formatRole, groupHeading, partyHeading, personSubtitle, profileHref,
} from './crmEntityRoles';
import { FactWhyButton } from './CrmFactEvidencePopover';
import { useCrmPartyFilter } from './crmPartyFilter';
import styles from './CrmPartiesField.module.css';

/** The chip's text: the role and its side, through the SAME vocabulary helpers the flat cells use, so
 * one register edit renames a role on every surface at once. A chip with no renderable role shows the
 * relationship type instead — a role-less participation is a legitimate claim (lever A) and must not
 * render as a blank. */
function chipLabel(entry: PartyRoleEntry): string {
  return groupHeading(entry.role, entry.party ?? null) ?? formatRole(entry.relationship_type);
}

/** WP-6b — the "why?" popover, PER CHIP, because a chip is one claim and the trace addresses one.
 *
 * The assertion ids come off the chip's own `members`, which the server already gated twice (the
 * entity gate on the endpoints, WP-5b's class gate on each claim), so a chip that reaches the render
 * carries only ids this viewer may ask about. A chip standing on two facts hands both over and the
 * popover switches between them — merging them would attribute one claim's emails to the other. */
function Chips({ roles, label }: { roles: PartyRoleEntry[]; label: string }) {
  const [showImplied, setShowImplied] = useState(false);
  const ordered = useMemo(() => sortRoleEntries(roles), [roles]);
  const plain = ordered.filter((r) => !r.implied);
  const implied = ordered.filter((r) => r.implied);
  const shown = showImplied ? ordered : plain;
  if (ordered.length === 0) return null;
  return (
    <span className={styles.chips}>
      {shown.map((entry) => (
        <span key={`${entry.relationship_type}|${entry.role ?? ''}`}
              className={entry.implied ? `${styles.chip} ${styles.chipImplied}` : styles.chip}>
          {chipLabel(entry)}
          <FactWhyButton assertionIds={entry.members.map((mb) => mb.assertion_id)}
                         label={`${label} — ${chipLabel(entry)}`} />
        </span>
      ))}
      {/* §5 rule 2 — an implied chip is HIDDEN, never dropped: its evidence still counts toward the
          row. Same "+N implied" affordance `CrmLinkedRecords` uses, so the two surfaces reveal the
          same thing the same way. */}
      {implied.length > 0 && (
        <button type="button" className={styles.toggle}
                onClick={() => setShowImplied((on) => !on)}>
          {showImplied ? 'hide implied' : `+${implied.length} implied`}
        </button>
      )}
    </span>
  );
}

/** The count for the chosen window, and nothing when there is none — a party with no visible mail
 * says nothing rather than "0", which reads as a measurement. */
function ActivityCount({ node, window: win }: { node: PartyPersonNode | PartyGroupNode;
                                                window: SortWindow }) {
  const n = node.activity?.[win] ?? 0;
  if (n <= 0) return null;
  // ONE text node, not `{n} email{s}` — an interpolated plural splits into three, and a split label
  // is neither copyable as a phrase nor findable by an accessible-name query.
  return <span className={styles.activity}>{`${n} email${n === 1 ? '' : 's'}`}</span>;
}

/** A `transaction_side` value as the desk says it — "Buy side", not "Buy". `partyHeading` returns null
 * for anything that is not a position, and the server never sends one, so the fallback is defensive. */
function sideLabel(side: string): string {
  return partyHeading(side) ?? formatRole(side);
}

/** P3 — the sides recorded here and at the counterpart cannot both be right. The whole sentence is
 * the accessible name: a bare ⚠ announces nothing to a screen reader. */
function ContradictionBadge({ node }: { node: PartyPersonNode | PartyGroupNode }) {
  const entries = node.contradiction ?? [];
  if (entries.length === 0) return null;
  const lines = entries.map((c) => {
    const who = c.counterpart.name
      ?? (c.counterpart.relation === 'firm' ? 'their firm' : 'someone at this firm');
    // The server decides WHICH disagreement this is; the client only phrases it. A `party` entry is a
    // PERSON against their firm across every discipline, so it names no axis — printing `role` there
    // would be printing `null`. A `discipline` entry is a firm at one shared axis, which it names.
    const lead = c.axis === 'discipline' && c.role ? formatRole(c.role) : 'Deal side';
    return `${lead} — recorded here as ${c.own_sides.map(sideLabel).join(' / ')}, `
      + `but ${who} is recorded as ${c.other_sides.map(sideLabel).join(' / ')}.`;
  });
  return <span className={styles.badge} role="img" title={lines.join(' ')}
               aria-label={`Side disagreement. ${lines.join(' ')}`}>⚠</span>;
}

/** The DISABLED twin of a live affordance, kept so the field's shape does not change when the thing
 * behind it goes live. PR-F took "why?" live (it is `FactWhyButton`, per chip, and renders its own
 * disabled form when there is no fact id); this is now only "show emails" outside a party-filter
 * provider — the grid, a story, a unit test of this field alone. Disabled rather than absent because
 * an affordance that appears later moves everything under it. */
function PendingAffordances({ label }: { label: string }) {
  return (
    <button type="button" className={styles.noop} disabled
            title={`${label} — available once the evidence reader ships`}>{label}</button>
  );
}

/** "show emails" (PR-G, spec lever F "entry points") — sets the timeline's party filter IN PLACE and
 * scrolls the section into view.
 *
 * ⛔ **TWO CONDITIONS, and the second one is the SERVER'S** (lead ruling 2026-09-05, on the intern's
 * F2). A provider must be mounted — outside one (the grid, a story, a unit test of this field alone)
 * there is nothing to act on — AND the server must say lever F is live, via `flags.party_filter` on
 * the per-viewer envelope. Provider presence alone was the old test and it was the wrong one:
 * `CrmRecordPanelContainer` mounts a provider UNCONDITIONALLY, so the button was live in exactly the
 * window the trace flag creates (`crm_fact_evidence_trace_enabled` ON, `crm_timeline_party_filter_enabled`
 * still OFF) and its click sent a `party` the server ignores.
 *
 * Not derived from the TIMELINE's payload, though that also carries the signal: the timeline fetches
 * lazily (`useOnceVisible`), so the chip would sit dead until the operator scrolled to a section they
 * may never open. The envelope arrives with the cell this button is drawn from.
 *
 * The node id is the tree's own, passed through untouched: this component assembles no filter value
 * of its own, which is what keeps the chip and the server's membership check speaking one
 * vocabulary. */
function ShowEmails({ nodeId, name, live }:
                    { nodeId: string; name: string | null; live: boolean }) {
  const party = useCrmPartyFilter();
  if (!party.available || !live) return <PendingAffordances label="show emails" />;
  return (
    <button type="button" className={styles.action} data-show-emails={nodeId}
            title={`Show only the emails ${name ?? 'this party'} is on — to, from or cc`}
            onClick={() => party.showOnly(nodeId)}>show emails</button>
  );
}

function PersonRow({ person, group, window: win, filterLive }:
                   { person: PartyPersonNode; group: PartyGroupNode; window: SortWindow;
                     /** The server's `flags.party_filter` — see `ShowEmails`. */
                     filterLive: boolean }) {
  const name = (person.person.name ?? '').trim() || '(unnamed)';
  const id = person.person.open_profile ? person.person.local_id : null;
  const subtitle = personSubtitle(person);
  // Lever D, "Rendering": a role chip ONLY where the person's set differs from the org's. A banker at
  // the sell-side bank repeating the firm's own chip is the noise this field exists to remove.
  const roles = distinctPersonRoles(person, group);
  return (
    <div className={styles.personRow}>
      {id != null ? (
        <a className={styles.link} href={profileHref('person', id)} target="_blank"
           rel="noopener noreferrer">{name}</a>
      ) : (
        <span>{name}</span>
      )}
      {subtitle && <span className={styles.subtitle}>, {subtitle}</span>}
      <Chips roles={roles} label={name} />
      <ContradictionBadge node={person} />
      <ActivityCount node={person} window={win} />
      {/* PR-F, G's as-built handoff — "show emails" on the PERSON row, not only on the firm. The
          person's own node id goes through untouched, so `person:41` reaches lever F's membership
          check as the tree's own value; "why?" now lives on each chip, where the claim it explains
          is. */}
      <ShowEmails nodeId={person.node_id} name={person.person.name ?? null}
                  live={filterLive} />
    </div>
  );
}

function GroupBlock({ group, window: win, filterLive }:
                    { group: PartyGroupNode; window: SortWindow; filterLive: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const people = expanded ? group.people : group.people.slice(0, PEOPLE_SHOWN);
  const hidden = group.people.length - people.length;
  const orgId = group.org?.open_profile ? group.org.local_id : null;
  return (
    <div className={styles.group}>
      <div className={styles.groupHead}>
        {group.org === null ? (
          // D-7: the trailing bucket for people whose firm is unknown or was withheld. A label, not a
          // company — it must never read as though a firm called "Individuals" is on the deal.
          <span className={styles.individualsHead}>Individuals</span>
        ) : orgId != null ? (
          <a className={`${styles.link} ${styles.orgName}`} href={profileHref('organization', orgId)}
             target="_blank" rel="noopener noreferrer">{group.org.name ?? '(unnamed)'}</a>
        ) : (
          <span className={styles.orgName}>{group.org.name ?? '(unnamed)'}</span>
        )}
        <Chips roles={group.roles} label={group.org?.name ?? 'Individuals'} />
        {/* "Harris Williams is the sell-side banker" versus "two people on this deal happen to work
            at Acme" — the ledger made one of those claims and not the other. */}
        {group.org !== null && !group.firm_role_recorded && (
          <span className={styles.viaPeople}>via its people</span>
        )}
        <ContradictionBadge node={group} />
        <ActivityCount node={group} window={win} />
        {group.org !== null && (
          <ShowEmails nodeId={group.node_id} name={group.org.name ?? null}
                      live={filterLive} />
        )}
      </div>
      {group.people.length > 0 && (
        <div className={styles.people}>
          {people.map((person) => (
            <PersonRow key={person.node_id} person={person} group={group} window={win}
                       filterLive={filterLive} />
          ))}
          {(hidden > 0 || expanded) && (
            <button type="button" className={styles.toggle}
                    onClick={() => setExpanded((on) => !on)}>
              {expanded ? 'show fewer' : `+${hidden} more`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function CrmPartiesField({ value, initialSort }:
                                { value: unknown;
                                  /** Test seam only — production reads the viewer's stored choice. */
                                  initialSort?: PartiesSort }) {
  const [sort, setSort] = useState<PartiesSort>(() => initialSort ?? loadPartiesSort());
  const choose = useCallback((next: PartiesSort) => {
    setSort(next);
    savePartiesSort(next);
  }, []);

  const tree = isPartyTree(value) && value.viewer_enriched ? value : null;
  const sorted = useMemo(() => (tree ? sortPartyTree(tree, sort) : null), [tree, sort]);
  // ABSENT ⇒ OFF. A payload written before the envelope carried `flags` cannot promise that a filter
  // exists to receive the click, and the safe direction for an affordance is the disabled one.
  const filterLive = tree?.flags?.party_filter === true;
  if (!sorted) return <span className={styles.empty}>—</span>;

  const windowLabel = (key: SortWindow, label: string) =>
    (key === 'lifetime' ? lifetimeLabel((sorted as PartyTreeValue).corpus_from) : label);

  return (
    <div className={styles.field}>
      <div className={styles.sortBar} role="group" aria-label="Sort parties">
        {(['role', 'activity'] as SortMode[]).map((mode) => (
          <span key={mode} className={styles.sortGroup}>
            <button type="button" aria-pressed={sort.mode === mode}
                    className={sort.mode === mode ? `${styles.sortBtn} ${styles.sortBtnOn}`
                                                  : styles.sortBtn}
                    onClick={() => choose({ ...sort, mode })}>
              {mode === 'role' ? 'By role' : 'By activity'}
            </button>
          </span>
        ))}
        {/* The window applies to the ACTIVITY numbers in both modes (role mode still shows a count and
            breaks its ties on it), so the chips stay visible rather than appearing on a mode switch
            and re-laying the control out under the cursor. */}
        <span className={styles.sortGroup}>
          {SORT_WINDOWS.map(({ key, label }) => (
            <button key={key} type="button" aria-pressed={sort.window === key}
                    title={key === 'lifetime' ? windowLabel(key, label) : undefined}
                    className={sort.window === key ? `${styles.sortBtn} ${styles.sortBtnOn}`
                                                   : styles.sortBtn}
                    onClick={() => choose({ ...sort, window: key })}>
              {label}
            </button>
          ))}
        </span>
      </div>
      {sorted.groups.map((group) => (
        <GroupBlock key={group.node_id} group={group} window={sort.window}
                    filterLive={filterLive} />
      ))}
    </div>
  );
}

export { DEFAULT_SORT, INDIVIDUALS_NODE_ID };
